import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import DragAndDrop from "components/FileUploadDrag/DragAndDrop";
import SummaryCard from "components/SummaryCard/SummaryCard";
import StarRating from "components/StarRating/StarRating";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DateInput from "components/DateInput/DateInput";
import TimeInput from "components/TimeInput/TimeInput";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { put } from "helpers/api_helper";
import * as api from "../../../constants/api_constants";
import { useNavigate } from "react-router-dom";
import { showSuccessToast } from "helpers/toastMessages";

const TechnicalRound = ({
  setEvaluationStatus,
  setActiveRound,
  evaluationStatus,
  candidatedetails,
  interviewStageId,
  jobId,
}) => {
  const navigate = useNavigate();

  // Helper function to normalize evaluation outcome
  const normalizeOutcome = (outcome) =>
    outcome
      ?.toLowerCase()
      ?.replace(/\s+/g, "_")
      ?.replace(/^move_to_next_round$/, "move_to_next_round") || "";

  // Helper function to map normalized outcome to local status
  const mapOutcomeToStatus = (normalizedOutcome) => {
    if (normalizedOutcome === "move_to_next_round") return "advance";
    if (normalizedOutcome === "put_on_hold") return "hold";
    if (normalizedOutcome === "reject") return "rejected";
    return "in progress";
  };

  // Helper function to find the technicalRoundDto by interviewStageId
  const getRelevantTechnicalRound = (interviews, stageId) => {
    if (!interviews || interviews.length === 0) {
      return null;
    }
    // First, try to find the technicalRoundDto matching the interviewStageId
    const matchingInterview = interviews.find(
      (interview) =>
        interview.interviewStageId === stageId && interview.technicalRoundDto
    );

    if (matchingInterview) {
      return matchingInterview.technicalRoundDto;
    }

    // Fallback: Select the first technicalRoundDto with actual data
    return (
      interviews
        .filter((interview) => interview.technicalRoundDto)
        .find((interview) => {
          const { technicalRoundDto } = interview;
          return (
            technicalRoundDto.problemSolving !== 0 ||
            technicalRoundDto.codingSkills !== 0 ||
            technicalRoundDto.systemDesign !== 0 ||
            technicalRoundDto.technicalKnowledge !== 0 ||
            technicalRoundDto.communication !== 0 ||
            technicalRoundDto.teamWork !== 0 ||
            technicalRoundDto.strongAreas?.length > 0 ||
            technicalRoundDto.areasForImprovement?.length > 0
          );
        })?.technicalRoundDto ||
      interviews.find((interview) => interview.technicalRoundDto)
        ?.technicalRoundDto ||
      null
    );
  };

  // Determine the relevant technicalRoundDto using interviewStageId
  const relevantTechnicalRound = getRelevantTechnicalRound(
    candidatedetails?.job?.[0]?.interviews,
    interviewStageId
  );

  // Determine if the component should be editable
  const isEditable =
    !interviewStageId ||
    (!relevantTechnicalRound ||
      relevantTechnicalRound.evaluationOutcome === null);

  const [onlineModalOpen, setOnlineModalOpen] = useState(false);
  const [offlineModalOpen, setOfflineModalOpen] = useState(false);
  const [scheduledMode, setScheduledMode] = useState(
    relevantTechnicalRound?.interviewMode?.toLowerCase() || null
  );
  const [evaluationRatings, setEvaluationRatings] = useState({
    problemSolving: relevantTechnicalRound?.problemSolving || 0,
    codingSkills: relevantTechnicalRound?.codingSkills || 0,
    systemDesign: relevantTechnicalRound?.systemDesign || 0,
    technicalKnowledge: relevantTechnicalRound?.technicalKnowledge || 0,
    communication: relevantTechnicalRound?.communication || 0,
    culturalFit: relevantTechnicalRound?.teamWork || 0,
  });
  const [localEvaluationStatus, setLocalEvaluationStatus] = useState(() => {
    const normalizedOutcome = normalizeOutcome(
      relevantTechnicalRound?.evaluationOutcome
    );
    return mapOutcomeToStatus(normalizedOutcome);
  });
  const [interviewDetails, setInterviewDetails] = useState({
    mode: relevantTechnicalRound?.interviewMode || "Online",
    date: relevantTechnicalRound?.interviewDate || "",
    time: relevantTechnicalRound?.interviewTime || "",
    platform: relevantTechnicalRound?.interviewPlatform || "Zoom",
    url: relevantTechnicalRound?.interviewUrl || "",
    location: relevantTechnicalRound?.interviewLocation || "",
    interviewers: relevantTechnicalRound?.interviewers || [],
    notify: false,
    scheduledBy: relevantTechnicalRound?.scheduledBy || "----",
  });
  const [strongAreas, setStrongAreas] = useState(
    relevantTechnicalRound?.strongAreas?.join(", ") || ""
  );
  const [areasForImprovement, setAreasForImprovement] = useState(
    relevantTechnicalRound?.areasForImprovement?.join(", ") || ""
  );
  const [attachments, setAttachments] = useState(
    relevantTechnicalRound?.attachments || []
  );

  // Refs for accessing Quill editor instances
  const strongAreasQuillRef = useRef(null);
  const areasForImprovementQuillRef = useRef(null);

  // Options for react-select
  const interviewerOptions = [
    { value: "Shad", label: "Shad" },
    { value: "Mohan", label: "Mohan" },
    { value: "Keshava", label: "Keshava" },
    { value: "Dathri", label: "Dathri" },
    { value: "Kowshik", label: "Kowshik" },
    { value: "Lokesh", label: "Lokesh" },
  ];

  // Update state when relevantTechnicalRound changes
  useEffect(() => {
    if (relevantTechnicalRound) {
      setEvaluationRatings({
        problemSolving: relevantTechnicalRound.problemSolving || 0,
        codingSkills: relevantTechnicalRound.codingSkills || 0,
        systemDesign: relevantTechnicalRound.systemDesign || 0,
        technicalKnowledge: relevantTechnicalRound.technicalKnowledge || 0,
        communication: relevantTechnicalRound.communication || 0,
        culturalFit: relevantTechnicalRound.teamWork || 0,
      });
      setInterviewDetails({
        mode: relevantTechnicalRound.interviewMode || "Online",
        date: relevantTechnicalRound.interviewDate || "",
        time: relevantTechnicalRound.interviewTime || "",
        platform: relevantTechnicalRound.interviewPlatform || "Zoom",
        url: relevantTechnicalRound.interviewUrl || "",
        location: relevantTechnicalRound.interviewLocation || "",
        interviewers: relevantTechnicalRound.interviewers || [],
        notify: false,
        scheduledBy: relevantTechnicalRound.scheduledBy || "----",
      });
      setStrongAreas(relevantTechnicalRound.strongAreas?.join(", ") || "");
      setAreasForImprovement(
        relevantTechnicalRound.areasForImprovement?.join(", ") || ""
      );
      setAttachments(relevantTechnicalRound.attachments || []);
      setScheduledMode(relevantTechnicalRound.interviewMode?.toLowerCase() || null);
      const normalizedOutcome = normalizeOutcome(
        relevantTechnicalRound.evaluationOutcome
      );
      setLocalEvaluationStatus(mapOutcomeToStatus(normalizedOutcome));
    }
  }, [relevantTechnicalRound]);

  const toggleOnlineModal = () => {
    setOnlineModalOpen(!onlineModalOpen);
    if (!onlineModalOpen) {
      setInterviewDetails((prev) => ({
        ...prev,
        mode: "Online",
        location: "",
      }));
    }
  };

  const toggleOfflineModal = () => {
    setOfflineModalOpen(!offlineModalOpen);
    if (!offlineModalOpen) {
      setInterviewDetails((prev) => ({
        ...prev,
        mode: "Offline",
        platform: "",
        url: "",
      }));
    }
  };

  // Handle changes for evaluation ratings
  const handleRatingChange = (category, value) => {
    if (isEditable) {
      setEvaluationRatings((prev) => ({
        ...prev,
        [category]: value,
      }));
    }
  };

  // Handle changes for evaluation outcome
  const handleEvaluationChange = async (status) => {
    if (!isEditable) return;

    setLocalEvaluationStatus(status);
    setEvaluationStatus(status);

    const evaluationOutcomeMap = {
      advance: "Move to next round",
      hold: "Put on hold",
      rejected: "Reject",
    };

    const evaluationOutcome = evaluationOutcomeMap[status] || "IN_PROGRESS";

    // Construct payload for API
    const payload = {
      delFlag: 1,
      jobId: jobId,
      candidateId: candidatedetails?.id,
      technicalRoundDto: {
        interviewMode: interviewDetails.mode.toUpperCase(),
        interviewDate: interviewDetails.date || null,
        interviewTime: interviewDetails.time || null,
        interviewPlatform:
          interviewDetails.mode === "Online" ? interviewDetails.platform : null,
        interviewUrl:
          interviewDetails.mode === "Online" ? interviewDetails.url : null,
        interviewLocation:
          interviewDetails.mode === "Offline" ? interviewDetails.location : null,
        interviewers: interviewDetails.interviewers.length
          ? interviewDetails.interviewers
          : ["Unknown"],
        problemSolving: evaluationRatings.problemSolving || 0,
        codingSkills: evaluationRatings.codingSkills || 0,
        systemDesign: evaluationRatings.systemDesign || 0,
        technicalKnowledge: evaluationRatings.technicalKnowledge || 0,
        communication: evaluationRatings.communication || 0,
        teamWork: evaluationRatings.culturalFit || 0,
        strongAreas: strongAreas
          ? strongAreas.split(",").map((item) => item.trim())
          : [],
        areasForImprovement: areasForImprovement
          ? areasForImprovement.split(",").map((item) => item.trim())
          : [],
        attachments: attachments.length ? attachments : [],
        evaluationOutcome,
        roundType: "Technical Round",
      },
    };

    try {
      const interviewId =
        interviewStageId ||
        relevantTechnicalRound?.technicalRoundId ||
        candidatedetails?.job?.[0]?.interviews?.[0]?.interviewStageId;

      if (!interviewId) {
        throw new Error("Interview ID is not available");
      }
      const response = await put(api.UPDATE_INTERVIEWS_BY_ID(interviewId), payload);
      if (status === "advance") {
        setActiveRound("hr/cultural");
      } else if (status === "hold" || status === "rejected") {
        navigate(`/job-details/${jobId}`);
      }
    } catch (error) {
      console.error("Failed to update technical round data:", error);
    }
  };

  // Handle scheduling interview submission
  const handleScheduleSubmit = async (mode) => {
    if (!isEditable) return;

    // Construct payload for API
    const payload = {
      delFlag: 1,
      jobId: jobId,
      candidateId: candidatedetails?.id,
      technicalRoundDto: {
        interviewMode: interviewDetails.mode.toUpperCase(),
        interviewDate: interviewDetails.date || null,
        interviewTime: interviewDetails.time || null,
        interviewPlatform:
          interviewDetails.mode === "Online" ? interviewDetails.platform : null,
        interviewUrl:
          interviewDetails.mode === "Online" ? interviewDetails.url : null,
        interviewLocation:
          interviewDetails.mode === "Offline" ? interviewDetails.location : null,
        interviewers: interviewDetails.interviewers.length
          ? interviewDetails.interviewers
          : ["Unknown"],
        problemSolving: evaluationRatings.problemSolving || 0,
        codingSkills: evaluationRatings.codingSkills || 0,
        systemDesign: evaluationRatings.systemDesign || 0,
        technicalKnowledge: evaluationRatings.technicalKnowledge || 0,
        communication: evaluationRatings.communication || 0,
        teamWork: evaluationRatings.culturalFit || 0,
        strongAreas: strongAreas
          ? strongAreas.split(",").map((item) => item.trim())
          : [],
        areasForImprovement: areasForImprovement
          ? areasForImprovement.split(",").map((item) => item.trim())
          : [],
        attachments: attachments.length ? attachments : [],
        evaluationOutcome: null, // Default for scheduling
      },
    };

    try {
      const interviewId =
        interviewStageId ||
        relevantTechnicalRound?.technicalRoundId ||
        candidatedetails?.job?.[0]?.interviews?.[0]?.interviewStageId;
      if (!interviewId) {
        throw new Error("Interview ID is not available");
      }
      const response = await put(api.UPDATE_INTERVIEWS_BY_ID(interviewId), payload);
      showSuccessToast("Interview scheduled successfully");
      setScheduledMode(mode.toLowerCase());
      if (mode === "Online") {
        setOnlineModalOpen(false);
      } else {
        setOfflineModalOpen(false);
      }
      navigate(`/job-details/${jobId}`);
    } catch (error) {
      console.error("Failed to schedule interview:", error);
    }
  };

  // Handle changes for interview details
  const handleInterviewChange = (field, value) => {
    if (isEditable) {
      setInterviewDetails((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Handle interviewer selection (multiple)
  const handleInterviewerChange = (selectedOptions) => {
    if (isEditable) {
      const interviewers = selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [];
      setInterviewDetails((prev) => ({
        ...prev,
        interviewers,
      }));
    }
  };

  // Reset interview details after submission
  const resetInterviewDetails = () => {
    setInterviewDetails({
      mode: "Online",
      date: "",
      time: "",
      platform: "Zoom",
      url: "",
      location: "",
      interviewers: [],
      notify: false,
      scheduledBy: "----",
    });
  };

  // Handle changes for Strong Areas (store plain text)
  const handleStrongAreasChange = (content, delta, source, editor) => {
    if (isEditable) {
      const plainText = editor.getText().trim();
      setStrongAreas(plainText);
    }
  };

  // Handle changes for Areas for Improvement (store plain text)
  const handleAreasForImprovementChange = (content, delta, source, editor) => {
    if (isEditable) {
      const plainText = editor.getText().trim();
      setAreasForImprovement(plainText);
    }
  };

  // Common toolbar configuration
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  // Handle file uploads from DragAndDrop (placeholder logic)
  const handleFileUpload = (files) => {
    if (isEditable) {
      const fileNames = files.map((file) => file.name);
      setAttachments((prev) => [...prev, ...fileNames]);
    }
  };

  return (
    <div>
      <Container fluid className="py-4 px-4">
        <Row>
          <Col>
            <SummaryCard title="Schedule Interview">
              <Row>
                <Col>
                  {!scheduledMode && isEditable && (
                    <>
                      <Button
                        onClick={toggleOnlineModal}
                        className="me-3"
                        disabled={!isEditable}
                        style={{
                          backgroundColor: "#475467",
                          color: "white",
                          borderRadius: "12px",
                          height: "50px",
                          width: "auto",
                        }}
                      >
                        Schedule Online Interview
                      </Button>
                      <Button
                        onClick={toggleOfflineModal}
                        className="w-auto"
                        disabled={!isEditable}
                        style={{
                          backgroundColor: "#475467",
                          color: "white",
                          borderRadius: "12px",
                          height: "50px",
                        }}
                      >
                        Schedule Offline Interview
                      </Button>
                    </>
                  )}

                  {scheduledMode === "online" && (
                    <Row className="m-3 mt-0">
                      <Col md={6}>
                        <p className="main1">Interview Mode</p>
                        <p className="main1">Interview Date & Time</p>
                        <p className="main1">Interviewer(s)</p>
                        <p className="main1">Interview Platform</p>
                        <p className="main1">Interview URL</p>
                        <p className="main1">Scheduled By</p>
                      </Col>
                      <Col md={6}>
                        <p>{interviewDetails.mode}</p>
                        <p>
                          {interviewDetails.date || "Not set"},{" "}
                          {interviewDetails.time || "Not set"}
                        </p>
                        <p>
                          {interviewDetails.interviewers.join(", ") || "None"}
                        </p>
                        <p>{interviewDetails.platform || "Not set"}</p>
                        <p>{interviewDetails.url || "Not set"}</p>
                        <p>{interviewDetails.scheduledBy}</p>
                      </Col>
                    </Row>
                  )}

                  {scheduledMode === "offline" && (
                    <Row className="m-3 mt-0">
                      <Col md={6}>
                        <p className="main1">Interview Mode</p>
                        <p className="main1">Interview Date & Time</p>
                        <p className="main1">Interviewer(s)</p>
                        <p className="main1">Location</p>
                        <p className="main1">Scheduled By</p>
                      </Col>
                      <Col md={6}>
                        <p>{interviewDetails.mode}</p>
                        <p>
                          {interviewDetails.date || "Not set"},{" "}
                          {interviewDetails.time || "Not set"}
                        </p>
                        <p>
                          {interviewDetails.interviewers.join(", ") || "None"}
                        </p>
                        <p>{interviewDetails.location || "Not set"}</p>
                        <p>{interviewDetails.scheduledBy}</p>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </SummaryCard>

            <SummaryCard title="Evaluation" className="text-black">
              <Row className="text-black">
                <Row className="mb-3">
                  <Col>
                    <h6>Problem Solving</h6>
                  </Col>
                  <Col>
                    <StarRating
                      value={evaluationRatings.problemSolving}
                      onChange={(value) =>
                        handleRatingChange("problemSolving", value)
                      }
                      disabled={!isEditable}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h6>Technical Knowledge</h6>
                  </Col>
                  <Col>
                    <StarRating
                      value={evaluationRatings.technicalKnowledge}
                      onChange={(value) =>
                        handleRatingChange("technicalKnowledge", value)
                      }
                      disabled={!isEditable}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h6>Communication</h6>
                  </Col>
                  <Col>
                    <StarRating
                      value={evaluationRatings.communication}
                      onChange={(value) =>
                        handleRatingChange("communication", value)
                      }
                      disabled={!isEditable}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h6>Cultural Fit / Teamwork</h6>
                  </Col>
                  <Col>
                    <StarRating
                      value={evaluationRatings.culturalFit}
                      onChange={(value) =>
                        handleRatingChange("culturalFit", value)
                      }
                      disabled={!isEditable}
                    />
                  </Col>
                </Row>
                <Row
                  style={{
                    backgroundColor: "#EAFAF1",
                    borderRadius: "12px",
                    padding: "16px",
                    margin: "16px",
                  }}
                >
                  <Row>
                    <h6>Strong Areas</h6>
                  </Row>
                  <div>
                    <ReactQuill
                      theme="snow"
                      value={strongAreas}
                      onChange={handleStrongAreasChange}
                      placeholder="Write about strong areas..."
                      modules={quillModules}
                      ref={strongAreasQuillRef}
                      readOnly={!isEditable}
                      style={{
                        fontSize: "14px",
                        color: "#333",
                        borderRadius: "10px",
                        backgroundColor: "#ffffff",
                      }}
                    />
                  </div>
                </Row>
                <Row
                  style={{
                    backgroundColor: "#FFF6F6",
                    borderRadius: "12px",
                    padding: "16px",
                    margin: "16px",
                  }}
                >
                  <Row>
                    <h6>Areas for Improvement</h6>
                  </Row>
                  <div>
                    <ReactQuill
                      theme="snow"
                      value={areasForImprovement}
                      onChange={handleAreasForImprovementChange}
                      placeholder="Write about areas for improvement..."
                      modules={quillModules}
                      ref={areasForImprovementQuillRef}
                      readOnly={!isEditable}
                      style={{
                        fontSize: "14px",
                        color: "#333",
                        borderRadius: "10px",
                        backgroundColor: "#ffffff",
                      }}
                    />
                  </div>
                </Row>
              </Row>
            </SummaryCard>

            <SummaryCard title="Attachments">
              <Col md={4}>
                <DragAndDrop
                  onFilesAdded={handleFileUpload}
                  disabled={!isEditable}
                />
              </Col>
              {attachments.length > 0 && (
                <div className="mt-3">
                  <h6>Uploaded Files:</h6>
                  <ul>
                    {attachments.map((file, index) => (
                      <li key={index}>{file}</li>
                    ))}
                  </ul>
                </div>
              )}
            </SummaryCard>

            <SummaryCard title="Evaluation Outcome">
              <Row>
                <Col>
                  <Button
                    onClick={() => handleEvaluationChange("rejected")}
                    disabled={!isEditable}
                    style={{
                      paddingRight: "40px",
                      paddingLeft: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      marginRight: "10px",
                      backgroundColor: "#F93131",
                      color: "white",
                      border:
                        localEvaluationStatus === "rejected"
                          ? "2px solid #000"
                          : "none",
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleEvaluationChange("hold")}
                    disabled={!isEditable}
                    style={{
                      paddingRight: "20px",
                      paddingLeft: "20px",
                      height: "40px",
                      borderRadius: "12px",
                      marginRight: "10px",
                      backgroundColor: "#1E9EFB",
                      color: "white",
                      border:
                        localEvaluationStatus === "hold"
                          ? "2px solid #000"
                          : "none",
                    }}
                  >
                    Put On Hold
                  </Button>
                  <Button
                    onClick={() => handleEvaluationChange("advance")}
                    disabled={
                      !isEditable ||
                      localEvaluationStatus === "rejected" ||
                      localEvaluationStatus === "hold"
                    }
                    style={{
                      paddingRight: "10px",
                      paddingLeft: "10px",
                      height: "40px",
                      borderRadius: "12px",
                      marginRight: "10px",
                      backgroundColor: "#006438",
                      color: "white",
                      border:
                        localEvaluationStatus === "advance"
                          ? "2px solid #000"
                          : "none",
                    }}
                  >
                    Move to Next Round
                  </Button>
                </Col>
              </Row>
            </SummaryCard>
          </Col>

          <Modal
            isOpen={onlineModalOpen}
            toggle={toggleOnlineModal}
            style={{
              maxWidth: "640px",
              marginTop: "60px",
              borderRadius: "32px",
              padding: "22px",
              gap: "24px",
              backgroundColor: "white",
            }}
            contentClassName="custom-modal-content"
          >
            <h4 className="text-black mb-3">Schedule Online Interview</h4>
            <FormGroup className="mb-3">
              <Label htmlFor="mode">Interview Mode</Label>
              <Input
                id="mode"
                name="mode"
                type="select"
                value={interviewDetails.mode}
                onChange={(e) => handleInterviewChange("mode", e.target.value)}
                disabled={!isEditable}
              >
                <option>Online</option>
              </Input>
            </FormGroup>
            <Row>
              <Col>
                <DateInput
                  label="Interview Date"
                  value={interviewDetails.date}
                  onChange={(e) =>
                    handleInterviewChange("date", e.target.value)
                  }
                  name="interview-date"
                  disabled={!isEditable}
                />
              </Col>
              <Col>
                <TimeInput
                  label="Interview Time"
                  value={interviewDetails.time}
                  onChange={(e) =>
                    handleInterviewChange("time", e.target.value)
                  }
                  name="interview-time"
                  disabled={!isEditable}
                />
              </Col>
            </Row>
            <FormGroup className="mb-3">
              <Label htmlFor="platform">Interview Platform</Label>
              <Input
                id="platform"
                name="platform"
                type="select"
                value={interviewDetails.platform}
                onChange={(e) =>
                  handleInterviewChange("platform", e.target.value)
                }
                disabled={!isEditable}
              >
                <option>Zoom</option>
                <option>Google Meet</option>
              </Input>
            </FormGroup>
            <FormGroup className="mb-3">
              <Label htmlFor="url">Interview URL</Label>
              <Input
                id="url"
                name="url"
                type="text"
                value={interviewDetails.url}
                onChange={(e) => handleInterviewChange("url", e.target.value)}
                disabled={!isEditable}
              />
            </FormGroup>
            <FormGroup className="mb-3">
              <Label htmlFor="interviewers">Interviewer(s)</Label>
              <Select
                id="interviewers"
                name="interviewers"
                isMulti
                options={interviewerOptions}
                value={interviewerOptions.filter((option) =>
                  interviewDetails.interviewers.includes(option.value)
                )}
                onChange={handleInterviewerChange}
                placeholder="Select Interviewers"
                classNamePrefix="react-select"
                isDisabled={!isEditable}
              />
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={interviewDetails.notify}
                  onChange={(e) =>
                    handleInterviewChange("notify", e.target.checked)
                  }
                  disabled={!isEditable}
                  style={{
                    transform: "scale(1.5)",
                    marginRight: "8px",
                    cursor: "pointer",
                    borderColor: "black",
                  }}
                />{" "}
                Notify candidate & Interviewers
              </Label>
            </FormGroup>
            <Row className="mt-2">
              <Col className="d-flex justify-content-end">
                <Button
                  className="me-1"
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    width: "128px",
                    height: "48px",
                    borderRadius: "12px",
                  }}
                  onClick={() => {
                    toggleOnlineModal();
                    resetInterviewDetails();
                  }}
                  disabled={!isEditable}
                >
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: "#006438",
                    color: "white",
                    width: "128px",
                    height: "48px",
                    borderRadius: "12px",
                  }}
                  onClick={() => handleScheduleSubmit("Online")}
                  disabled={!isEditable}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Modal>
          <Modal
            isOpen={offlineModalOpen}
            toggle={toggleOfflineModal}
            style={{
              maxWidth: "640px",
              marginTop: "100px",
              borderRadius: "32px",
              padding: "22px",
              gap: "24px",
              backgroundColor: "white",
            }}
            contentClassName="custom-modal-content"
          >
            <h4 className="text-black mb-3">Schedule Offline Interview</h4>
            <FormGroup className="mb-3">
              <Label htmlFor="mode">Interview Mode</Label>
              <Input
                id="mode"
                name="mode"
                type="select"
                value={interviewDetails.mode}
                onChange={(e) => handleInterviewChange("mode", e.target.value)}
                disabled={!isEditable}
              >
                <option>Offline</option>
              </Input>
            </FormGroup>
            <Row>
              <Col>
                <DateInput
                  label="Interview Date"
                  value={interviewDetails.date}
                  onChange={(e) =>
                    handleInterviewChange("date", e.target.value)
                  }
                  name="date"
                  disabled={!isEditable}
                />
              </Col>
              <Col>
                <TimeInput
                  label="Interview Time"
                  value={interviewDetails.time}
                  onChange={(e) =>
                    handleInterviewChange("time", e.target.value)
                  }
                  name="time"
                  disabled={!isEditable}
                />
              </Col>
            </Row>
            <FormGroup className="mb-3">
              <Label htmlFor="location">Interview Location</Label>
              <Input
                id="location"
                name="location"
                type="text"
                value={interviewDetails.location}
                onChange={(e) =>
                  handleInterviewChange("location", e.target.value)
                }
                disabled={!isEditable}
              />
            </FormGroup>
            <FormGroup className="mb-3">
              <Label htmlFor="interviewers">Interviewer(s)</Label>
              <Select
                id="interviewers"
                name="interviewers"
                isMulti
                options={interviewerOptions}
                value={interviewerOptions.filter((option) =>
                  interviewDetails.interviewers.includes(option.value)
                )}
                onChange={handleInterviewerChange}
                placeholder="Select Interviewers"
                classNamePrefix="react-select"
                isDisabled={!isEditable}
              />
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={interviewDetails.notify}
                  onChange={(e) =>
                    handleInterviewChange("notify", e.target.checked)
                  }
                  disabled={!isEditable}
                  style={{
                    transform: "scale(1.5)",
                    marginRight: "8px",
                    cursor: "pointer",
                    borderColor: "black",
                  }}
                />{" "}
                Notify candidate & Interviewers
              </Label>
            </FormGroup>
            <Row className="mt-2">
              <Col className="d-flex justify-content-end">
                <Button
                  className="me-1"
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    width: "128px",
                    height: "48px",
                    borderRadius: "12px",
                  }}
                  onClick={() => {
                    toggleOfflineModal();
                    resetInterviewDetails();
                  }}
                  disabled={!isEditable}
                >
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: "#006438",
                    color: "white",
                    width: "128px",
                    height: "48px",
                    borderRadius: "12px",
                  }}
                  onClick={() => handleScheduleSubmit("Offline")}
                  disabled={!isEditable}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Modal>
        </Row>
      </Container>
    </div>
  );
};

export default TechnicalRound;